import { CustomProfileFieldType, type FieldOption, type CustomProfileField } from '@logto/schemas';
import { condString } from '@silverhand/essentials';
import { useEffect, useState } from 'react';
import { Controller, FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import * as s from 'superstruct';

import Button from '@/components/Button';
import PrimitiveProfileInputField from '@/components/InputFields/PrimitiveProfileInputField';
import { provinces } from '@/data/provinces';
import { trackEvent } from '@/utils/analytics';
import Folder from '@/ui-assets/images/folder.png';
import { getRuntimeConfig } from '@/utils/config';

import AddressSubForm from './AddressSubForm';
import FullnameSubForm from './FullnameSubForm';
import styles from './index.module.scss';
import useFieldLabel from './use-field-label';
import useValidateField from './use-validate-field';

type Props = {
  readonly customProfileFields: CustomProfileField[];
  readonly defaultValues?: Record<string, unknown>;
  readonly onSubmit: (values: Record<string, unknown>) => Promise<void>;
  readonly isSocialLogin?: boolean;
};

// Fields ที่ต้อง required ตามประเภทการ register
const socialRequiredFields = ['gender', 'province', 'birthYear'];
const emailPhoneRequiredFields = ['name', 'gender', 'province', 'birthYear'];

type ApiState = 'idle' | 'success' | 'loading' | 'error';

interface EmailSettingItem {
  key: string;
  title: string;
  description: string;
  previewTitle: string;
  previewImage: string;
}


const ExtraProfileForm = ({ customProfileFields, defaultValues, onSubmit, isSocialLogin = false }: Props) => {
  const getFieldLabel = useFieldLabel();
  const validateField = useValidateField();
  const methods = useForm<Record<string, unknown>>({
    reValidateMode: 'onBlur',
    defaultValues,
  });
  const { t } = useTranslation();

  const [isSkipping, setIsSkipping] = useState(false);
  const [loadJsonApiState, setLoadJsonApiState] = useState<ApiState>('loading');
  const [allEmailSettings, setAllEmailSettings] = useState<EmailSettingItem[]>([]);

  const {
    handleSubmit,
    control,
    formState: { errors, isSubmitting },
    getValues,
  } = methods;

  const getRecomputedEmailSubscription = (value: Record<string, unknown>) => {
    /**
     * isSubscribeDailyDigest is a field we set up at LOGTO_CONSOLE_URL/console/sign-in-experience/collect-user-profile
     * This field will be labeled as "รับการแจ้งเตือนเกี่ยวกับข่าวสารและกิจกรรมจาก ส.ส.ท."
     * After check/unchecking this field, ensure all hidden fields are updated too.
     * */
    const isCheckedSubscribeEmails = value.isSubscribeDailyDigest === true || value.isSubscribeDailyDigest === 'true';

    return allEmailSettings.reduce<Record<string, boolean>>((prev, next) => {
      prev[next.key] = isCheckedSubscribeEmails;
      return prev;
    }, {});
  };

  const submit = handleSubmit(async (value) => {
    const newValue = {
      ...value,
      ...getRecomputedEmailSubscription(value),
    };

    await onSubmit(newValue);
  });

  const handleSkip = async () => {
    trackEvent(
      'extra_profile_form',
      'skipped',
      { eventId: 'extra_profile_form_skipped' },
      {}
    );

    const allEmailsSubscribed = allEmailSettings.reduce<Record<string, boolean>>((prev, next) => {
      prev[next.key] = true;
      return prev;
    }, {});

    // Get the raw form values without validation
    const formValues = getValues();
    const newValue = {
      ...formValues,
      ...allEmailsSubscribed,
    };

    setIsSkipping(true);
    try {
      await onSubmit(newValue);
    } finally {
      setIsSkipping(false);
    }
  };

  // กำหนด required fields ตามประเภทการ register
  // Social: gender, province, birthYear (ไม่รวม name เพราะใช้จาก social)
  // Email/Phone: name, gender, province, birthYear
  const requiredFieldNames = isSocialLogin ? socialRequiredFields : emailPhoneRequiredFields;
  const allRequiredFields = customProfileFields.map((f) => ({
    ...f,
    required: requiredFieldNames.includes(f.name),
  }));

  useEffect(() => {
    async function fetchJsonFile() {
      const config = getRuntimeConfig();
      const baseUrl = config.VITE_S3_STORAGE_URL;
      setLoadJsonApiState('loading');

      try {
        const response = await fetch(`${baseUrl}/edit-profile/email-settings-th.json`);

        if (!response.ok) {
          setLoadJsonApiState('error');
          console.error('Failed to fetch "email-settings-th.json"');
          return;
        }

        const allEmailSettings: EmailSettingItem[] = await response.json();

        setAllEmailSettings(allEmailSettings);
        setLoadJsonApiState('success');
      } catch (error) {
        console.error(error);
        setLoadJsonApiState('error');
      }
    }

    fetchJsonFile();
  }, []);

  if (loadJsonApiState === 'error') {
    return (
      <div className={styles.errorContainer}>
        <p className={styles.errorText}>
          {t('error.load_email_settings_error')}
        </p>
      </div>
    );
  }

  return (
    <FormProvider {...methods}>
      <img src={Folder} className={styles.decoration} alt="" width={93} height={93} />
      <form className={styles.form} autoComplete="off" onSubmit={submit}>
        {allRequiredFields.map((field) => {
          if (field.type === CustomProfileFieldType.Fullname) {
            return <FullnameSubForm key={field.name} field={field} />;
          }
          const { name, type, label, description, required } = field;
          return (
            <Controller
              key={name}
              control={control}
              name={name}
              rules={{
                validate: (value) =>
                  validateField(value, { ...field, required, description: condString(description) }),
              }}
              render={({ field: { onBlur, onChange, value } }) => {
                if (type === CustomProfileFieldType.Address) {
                  return <AddressSubForm field={field} />;
                }

                s.assert(value, s.optional(s.string()));

                const isSubscribeEmailField =
                  type === CustomProfileFieldType.Checkbox && name === 'isSubscribeDailyDigest';
                const isHiddenSubscribeEmailField =
                  type === CustomProfileFieldType.Checkbox && name !== 'isSubscribeDailyDigest';
                const isBirthYearField =
                  type === CustomProfileFieldType.Select && name === 'birthYear';
                const isProvinceField =
                  type === CustomProfileFieldType.Select && name === 'province';

                let fieldLabel = label || getFieldLabel(name);
                let customOptions: FieldOption[] | undefined;

                if (isSubscribeEmailField) {
                  fieldLabel = t('action.email_subscribe_title');
                }

                if (isBirthYearField) {
                  const currentYear = new Date().getFullYear();
                  const startYear = 1917; // พ.ศ. 2460
                  const minimumAge = 7;
                  const yearRange = currentYear - minimumAge - startYear + 1;

                  customOptions = Array.from({ length: yearRange }, (_, i) => ({
                    value: String(startYear + i),
                    label: String(startYear + 543 + i),
                  })).toReversed();
                }

                if (isProvinceField) {
                  customOptions = provinces;
                }

                return (
                  <div className={styles.inputGroup}>
                    {!isSubscribeEmailField && false && (
                      <label className={styles.label} htmlFor={name}>
                        {fieldLabel}
                      </label>
                    )}
                    <PrimitiveProfileInputField
                      {...field}
                      name={name}
                      label={fieldLabel}
                      description={condString(description)}
                      isDanger={!!errors[name]}
                      errorMessage={errors[name]?.message}
                      customOptions={customOptions}
                      hidden={isHiddenSubscribeEmailField}
                      onChange={onChange}
                      onBlur={onBlur}
                      required={required}
                      // required={true}
                      value={value}
                    />
                    {isSubscribeEmailField && (
                      <p className={styles.fieldDescription}>
                        {t('action.email_subscribe_description')}
                      </p>
                    )}
                  </div>
                );
              }}
            />
          );
        })}
        <Button
          className={styles.submitButton}
          title="action.continue"
          htmlType="submit"
          isLoading={isSubmitting}
          disabled={isSubmitting || isSkipping}
        />
        <Button
          className={styles.skipButton}
          title="action.skip"
          type="secondary"
          htmlType="button"
          isLoading={isSkipping}
          disabled={isSubmitting || isSkipping}
          onClick={handleSkip}
        />
        <input hidden type="submit" />
      </form>
    </FormProvider>
  );
};

export default ExtraProfileForm;
